import React from 'react';
import capitalize from 'lodash/capitalize';
import QueryString from 'query-string';
import { useSelector, useDispatch } from 'react-redux';
import { Padding } from 'components/base/layout-components';
import TabList from 'components/TabList';
import { PROVIDERS_MAP } from 'common/constants/constants';
import AppRouter from 'common/AppRouter';
import PMIncidentsSettings from 'views/root/views/home/views/settings/views/ProjectManagement/connected-components/PMConfiguration/components/PMIncidentsSettings/index';
import PMIterations from 'views/root/views/home/views/settings/views/ProjectManagement/connected-components/PMConfiguration/components/PMIterations/PMIterations';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { allProjectsSelector } from 'state/selectors/projects';
import {
  updateProject as updateProjectCaller,
  getDefaultIterations as getDefaultIterationsCaller,
  DateSelectionChanged as DateSelectionChangedCaller
} from 'state/callers';
import {
  isTeamLicenseSelector,
  retentionDaysSelector
} from 'state/selectors/license';
import { getSensorAuthorizations } from 'state/selectors/sensors-authorizations';
import { getManagementTool } from 'common/utils/utils';
import { hasIterationsSelector } from 'state/selectors/ui-state';
import useSettingsService from 'components/hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';

const PROJECT_MANAGEMENT_PATH = AppRouter.PROJECT_MANAGEMENT.path;
const PROJECT_MANAGEMENT_PROJECTS_PATH =
  AppRouter.PROJECT_MANAGEMENT_PROJECTS.path;

const Tabs = ({
  toggleSnackBar,
  toggleModal,
  managementTool,
  getPmComponent,
  isAuthorized,
  selectedTeam,
  apiTokens,
  setApiTokens,
  isOrgSettings
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { getSettingOrDefinitionValue } = useSettingsService();
  const updateProject = (selectedTeamProjectId, payload) =>
    dispatch(updateProjectCaller(selectedTeamProjectId, payload));
  const getDefaultIterations = (params, options) =>
    dispatch(getDefaultIterationsCaller(params, options));
  const DateSelectionChanged = params =>
    dispatch(DateSelectionChangedCaller(params));
  const sensorAuthorizations = useSelector(getSensorAuthorizations);
  const sensorAuth = getManagementTool({
    sensorAuthorizations
  });
  const retentionDays = useSelector(retentionDaysSelector);

  const projects = useSelector(allProjectsSelector);
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const hasIterations = useSelector(hasIterationsSelector);

  const isPMToolsEnabled = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.isPMToolsEnabled
  });

  const onTabClick = ({ path }) => history.push(path);

  const shouldShowJiraProjects =
    sensorAuth &&
    managementTool === PROVIDERS_MAP.jira.provider_path &&
    isAuthorized;

  const tabs = [
    ...(isPMToolsEnabled && !isAuthorized && !PROVIDERS_MAP[managementTool]
      ? [
          {
            name: 'Projects',
            Component: getPmComponent(),
            path: `${PROJECT_MANAGEMENT_PROJECTS_PATH}${
              isOrgSettings ? '?isOrgSettings=true' : ''
            }`
          }
        ]
      : []),
    ...(isPMToolsEnabled && isOrgSettings && shouldShowJiraProjects
      ? [
          {
            name: 'Projects',
            Component: getPmComponent(
              PROVIDERS_MAP[managementTool].entity_names.project
            ),
            path: `${PROJECT_MANAGEMENT_PATH}/jira/project?isOrgSettings=true`
          }
        ]
      : []),
    ...(isPMToolsEnabled &&
    (isOrgSettings || !isAuthorized) &&
    PROVIDERS_MAP[managementTool]
      ? [
          {
            name: !isAuthorized
              ? 'Projects'
              : capitalize(PROVIDERS_MAP[managementTool].entity_name_plural),
            Component: getPmComponent(
              PROVIDERS_MAP[managementTool].entity_name
            ),
            type: 'default',
            path: `${PROJECT_MANAGEMENT_PATH}${
              isOrgSettings ? '?isOrgSettings=true' : ''
            }`
          }
        ]
      : []),
    ...(isPMToolsEnabled && !isOrgSettings && isAuthorized
      ? [
          {
            name: 'Iterations',
            type: 'default',
            path: `${AppRouter.PROJECT_MANAGEMENT.path}/${managementTool}/iterations`,
            Component: (
              <div style={{ paddingTop: '2.5rem' }}>
                <PMIterations
                  selectedTeam={selectedTeam}
                  toggleSnackBar={toggleSnackBar}
                  projects={projects}
                  updateProject={updateProject}
                  isTeamLicense={isTeamLicense}
                  sensorAuth={sensorAuth}
                  getIterations={getDefaultIterations}
                  retentionDays={retentionDays}
                  DateSelectionChanged={DateSelectionChanged}
                  hasIterations={hasIterations}
                  pmToolConfig={PROVIDERS_MAP[managementTool.toLowerCase()]}
                />
              </div>
            )
          }
        ]
      : []),

    {
      name: 'Incidents',
      Component: (
        <PMIncidentsSettings
          selectedTeam={selectedTeam}
          apiTokens={apiTokens}
          setApiTokens={setApiTokens}
          toggleModal={toggleModal}
          toggleSnackBar={toggleSnackBar}
          isOrgSettings={isOrgSettings}
        />
      ),
      path: `${PROJECT_MANAGEMENT_PATH}/${managementTool}/incidents${
        isOrgSettings ? '?isOrgSettings=true' : ''
      }`
    }
  ];

  const selectedTab =
    tabs.find(
      ({ path }) =>
        QueryString.parseUrl(history.location.pathname).url ===
        QueryString.parseUrl(path).url
    ) ||
    tabs.find(({ type }) => type === 'default') ||
    (tabs.length > 0 ? tabs[0] : null);

  return (
    <>
      {tabs.length > 1 ? (
        <Padding l='medium' r='medium'>
          <TabList
            display='inSpacedSection'
            tabs={tabs}
            onTabClick={onTabClick}
            selectedTab={selectedTab ? selectedTab.name : ''}
          />
        </Padding>
      ) : null}

      {selectedTab?.Component}
    </>
  );
};

export default Tabs;
